<template>
  <div class="filter-page" :class="{ 'is-mobile': isMobile }">
    <div class="filter-page__container">
      <Banner
        v-if="isSubPagesFetched"
        v-show="!isLoading"
        :desktopSrc="bannerData.desktopSrc"
        :mobileSrc="bannerData.mobileSrc"
        :isLarge="bannerData.hasTopBannerItem"
      >
        <BannerContent v-if="bannerData.hasTopBannerItem" :content="topBanner" />
      </Banner>
      <FeaturedArticles
        v-if="isDataFetched && !hasArticlesPageBottom"
        v-show="!isLoading"
        :featuredArticles="featuredArticles"
      />
      <transition name="fade">
        <div class="filter-page__bar" v-if="!isMobile && !isLoading">
          <Breadcrumbs class="filter-page__breadcrumbs">
            <Breadcrumb
              :breadcrumbLink="hostname"
              :breadcrumbName="$t('filter.breadcrumb.home')"
              :isSeparatorVisible="Boolean(breadcrumbs.length)"
            />
            <Breadcrumb
              v-for="(breadcrumb, i) in breadcrumbs"
              :key="`${breadcrumb.name}-${i}`"
              :breadcrumbLink="breadcrumb.link"
              :breadcrumbName="breadcrumb.name"
              :isSeparatorVisible="Boolean(i + 1 < breadcrumbs.length)"
            />
          </Breadcrumbs>
          <Tags />
          <Sorting />
        </div>
      </transition>
      <div class="filter-page__content">
        <LoadingIndicator
          v-if="isLoading"
          class="filter-page__loader"
          v-data-test-id="'filter-page-loader'"
        />
        <div
          v-if="haveInitialProductsBeenRendered"
          v-show="!isLoading"
          class="filter-page__content-ready"
          v-data-test-id="'filter-page-content'"
        >
          <Filters />
          <Products
            :isExplodedVariants="hasExplodedVariants"
            @before-last-card-enter.once="restoreLastScrollPositionIfAny"
          />
        </div>
      </div>
      <FeaturedArticles
        v-if="isDataFetched && hasArticlesPageBottom"
        v-show="!isLoading"
        :featuredArticles="featuredArticles"
      />
    </div>
  </div>
</template>

<script>
// TODO: Remove once DY-test ended
// https://adm.dynamicyield.com/r?locale=en&redirectToSectionId=8772797#/smarts/api/496336
import DynamicYieldService from '@/api/services/_shared/dynamic-yield-service.js'
import 'url-polyfill'
import 'url-search-params-polyfill'
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import filterStore from '@/stores/filter'
import pageStore from '@/stores/page'
import viewportStore from '@/stores/viewport'
import storeStore from '@/stores/store'
import { EventBus, EVENTS } from '@/services/event-bus'
import { registerStores } from '@/helpers/stores'
import { preventScrollRestoration } from '@/helpers/scroll'
import { waitFor } from '@/helpers/time'
import { DEFAULT_ITEMS_PER_PAGE } from '@/components/FilterPage/constants'
import Banner from '@/components/FilterPage/Banner/Banner.vue'
import BannerContent from '@/components/FilterPage/Banner/BannerContent/BannerContent.vue'
import FeaturedArticles from '@/components/FilterPage/FeaturedArticles/FeaturedArticles.vue'
import Breadcrumb from '@/components/_shared/Breadcrumbs/Breadcrumb.vue'
import Breadcrumbs from '@/components/_shared/Breadcrumbs/Breadcrumbs.vue'
import Tags from '@/components/FilterPage/Tags/Tags.vue'
import Sorting from '@/components/FilterPage/Sorting/Sorting.vue'
import LoadingIndicator from '@/components/_shared/LoadingIndicator.vue'
import Filters from '@/components/FilterPage/Filters/Filters.vue'
import Products from '@/components/FilterPage/Products/Products.vue'
import {
  storeScrollPositionInHistoryState,
  restoreLastScrollPositionIfAny,
  isRestoringScroll,
} from '@/components/FilterPage/scroll-restoration-helpers'

// TODO: Remove once DY-test ended
// https://adm.dynamicyield.com/r?locale=en&redirectToSectionId=8772797#/smarts/api/496336
const CAMPAIGN_ID = 'cardGalleryTest'

export default {
  name: 'FilterPage',

  components: {
    Banner,
    BannerContent,
    FeaturedArticles,
    Breadcrumb,
    Breadcrumbs,
    Tags,
    Sorting,
    LoadingIndicator,
    Filters,
    Products,
  },

  data() {
    return {
      DEFAULT_ITEMS_PER_PAGE,
      haveInitialProductsBeenRendered: false,
    }
  },

  computed: {
    ...mapState('filter', [
      'areProductsFetched',
      'isSubPagesFetched',
      'isDataFetched',
      'isPromoCardsFetched',
      'hasExplodedVariants',
      'hasArticlesPageBottom',
      'isStickyMenuMounted',
      'isUpdatedByFilter',
      'featuredArticles',
      'topBanner',
      'products',
    ]),

    ...mapGetters({
      hostname: 'page/hostname',
      isMobile: 'filter/isMobile',
      viewportWidth: 'viewport/width',
      viewportDimensions: 'viewport/dimensions',
      activePrimaryFilters: 'filter/activePrimaryFilters',
      activeSubPage: 'filter/activeSubPage',
    }),

    bannerData() {
      return {
        desktopSrc: this.activeSubPage?.imageUrl || '',
        mobileSrc: this.activeSubPage?.mobileImageUrl || this.activeSubPage?.imageUrl || '',
        hasTopBannerItem: this.topBanner?.item?.variants.length > 0,
      }
    },

    /**
     * "isLoading" should only control the initial page load. The "Products" component has a
     * different loading indicator which shows up on filter change. To avoid conflicts when
     * switching "areProductsFetched" back to false, "isLoading" here will only wait for the
     * initial products to be fetched (this.haveInitialProductsBeenRendered). This way,
     * "isLoading" will stay false when fetching the products for the new filter applied.
     */
    isLoading() {
      return (
        !this.haveInitialProductsBeenRendered ||
        !this.isSubPagesFetched ||
        !this.isStickyMenuMounted
      )
    },

    areProductsRendered() {
      return (
        this.areProductsFetched &&
        (!isRestoringScroll(this.viewportDimensions) || this.isPromoCardsFetched)
      )
    },

    breadcrumbs() {
      return this.activePrimaryFilters.map(filter => ({
        name: filter.options.find(option => option.value === filter.values[0]).label,
        link: On.filterPage.basePath + filter.values.join('/'),
      }))
    },
  },

  watch: {
    viewportWidth: {
      immediate: true,
      handler(newWidth) {
        this.setPromoCardsPositions(newWidth)
      },
    },
  },

  beforeRouteUpdate(to, from, next) {
    if (!this.isUpdatedByFilter) this.refreshFilterPage({ route: to })
    next()
  },

  beforeCreate() {
    // If supported don't jump to restored scroll position on popstate
    preventScrollRestoration()

    registerStores({
      filter: filterStore,
      page: pageStore,
      viewport: viewportStore,
      store: storeStore,
    })
  },

  created() {
    this.setInitialProductsRendered()
    this.setBasePath(On.filterPage.basePath)
    this.init()
    // main navigation event bus
    window.addEventListener('popstate', this.handleMainNavigation)
    EventBus.$on(EVENTS.filter.navigationToOtherFilterPage, this.handleMainNavigation)
    window.addEventListener('beforeunload', this.storeScrollPositionInHistoryState)
    this.setDyCampaignData()
  },

  mounted() {
    document.body.classList.add('filter-page-is-mounted')
  },

  beforeDestroy() {
    // main navigation event bus
    window.removeEventListener('popstate', this.handleMainNavigation)
    EventBus.$off(EVENTS.filter.navigationToOtherFilterPage, this.handleMainNavigation)
    window.removeEventListener('beforeunload', this.storeScrollPositionInHistoryState)
  },

  methods: {
    ...mapActions('filter', ['initFilterPage', 'refreshFilterPage']),

    ...mapMutations('filter', ['setPromoCardsPositions', 'setBasePath', 'setUpdatedByFilter']),

    ...mapMutations('featureToggle', ['addFeature']),

    init() {
      /**
       * How it works
       *
       * 1. Fetch Filters options
       *    - Get filters/sorting from URL
       * 2. Async Fetch
       *    - Products (filtered/sorted)
       *    - PromoCards (filtered)
       *    - SubPages (filtered)
       *    - TopBanner
       *    - Featured Articles
       * 3. Subscribe to Mutations
       *    - Active Filters: Fetch Products/Promocards/SubPages
       *    - Active Sorting: Fetch Products
       */
      this.initFilterPage({ route: this.$route })
    },

    /**
     * This method will be triggered as callback
     * by the main navigation click via event bus
     *
     * @param {String} href = undefined
     * @param {Boolean} isNavigation = true
     */
    handleMainNavigation({ href, isNavigation = true }) {
      if (!href) return
      // setup fake $route for update
      const url = new URL(href)
      const pathMatch = url.pathname.replace(On.filterPage.basePath, '')
      const query = {}
      for (var [key, value] of url.searchParams.entries()) query[key] = value

      const route = {
        params: {
          pathMatch,
        },
        query,
      }

      this.refreshFilterPage({ route, isNavigation })
    },

    async setInitialProductsRendered() {
      this.haveInitialProductsBeenRendered = await waitFor(this, () => this.areProductsRendered)
    },

    // TODO: Remove once DY-test ended
    // https://adm.dynamicyield.com/r?locale=en&redirectToSectionId=8772797#/smarts/api/496336
    async setDyCampaignData() {
      const [{ campaigns }] = await Promise.all([
        DynamicYieldService.fetchCampaign(['product-card-gallery'], {
          isDesktop: String(!this.isMobile),
        }),
      ])

      if (campaigns?.length) {
        this.addFeature({
          id: CAMPAIGN_ID,
          condition: () => campaigns[0].variation.isCardGalleryTest,
          campaignIds: campaigns[0].campaignIds,
        })
      }
    },

    storeScrollPositionInHistoryState() {
      storeScrollPositionInHistoryState(this.viewportDimensions)
    },

    restoreLastScrollPositionIfAny(cardsComponent) {
      restoreLastScrollPositionIfAny(cardsComponent, this.viewportDimensions)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/components/FilterPage/FilterPage';
</style>
